import { useEffect, useRef } from 'react';

/**
 * Enhanced useInterval hook that automatically pauses when the browser tab is not visible/active
 * This improves performance by preventing unnecessary API calls when user is not viewing the tab
 *
 * @param {Function} callback - Function to call on interval
 * @param {number} delay - Delay in milliseconds, or null to disable
 * @param {Object} options - Configuration options
 * @param {boolean} options.pauseOnHidden - Whether to pause when tab is hidden (default: true)
 * @param {boolean} options.runImmediatelyOnVisible - Whether to run immediately when tab becomes visible (default: false)
 */
export function useVisibilityAwareInterval(callback, delay, options = {}) {
  const { pauseOnHidden = true, runImmediatelyOnVisible = false } = options;

  const savedCallback = useRef();
  const intervalRef = useRef();
  const wasHiddenRef = useRef(false);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval with visibility handling
  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    function handleVisibilityChange() {
      const isHidden = document.hidden;

      if (pauseOnHidden && delay !== null) {
        if (isHidden) {
          // Tab became hidden - pause the interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          wasHiddenRef.current = true;
        } else {
          // Tab became visible - resume the interval
          if (wasHiddenRef.current) {
            // Run immediately if configured to do so
            if (runImmediatelyOnVisible) {
              tick();
            }

            // Start the interval again
            intervalRef.current = setInterval(tick, delay);
            wasHiddenRef.current = false;
          }
        }
      }
    }

    // Initial setup
    if (delay !== null) {
      // Only start interval if tab is visible or if we don't care about visibility
      if (!pauseOnHidden || !document.hidden) {
        intervalRef.current = setInterval(tick, delay);
      }

      // Listen for visibility changes
      if (pauseOnHidden) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (pauseOnHidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [delay, pauseOnHidden, runImmediatelyOnVisible]);

  // Manual control functions (optional)
  const pause = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const resume = () => {
    if (delay !== null && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        if (savedCallback.current) {
          savedCallback.current();
        }
      }, delay);
    }
  };

  return { pause, resume };
}

/**
 * Backward-compatible wrapper that behaves like the original useInterval
 * but with visibility awareness enabled by default
 */
export function useInterval(callback, delay) {
  return useVisibilityAwareInterval(callback, delay, {
    pauseOnHidden: true,
    runImmediatelyOnVisible: false,
  });
}

export default useVisibilityAwareInterval;
